import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Speaker, CheckCircle2, AlertTriangle, ZapOff, Zap } from 'lucide-react';
import api from '../../utils/api';
import CompanyLogo from '../../components/CompanyLogo';

export const meta = {
  id: 'sonos_fleet',
  category: 'system',
  titleKey: 'dashboard.widgets.sonosFleet',
  descriptionKey: 'dashboard.widgets.sonosFleetDesc',
  defaultSize: 'sm',
  requiresModule: 'monitor',
  icon: Speaker,
};

// Company-scoped automatically: /sonos/summary filters by req.user.companyScope,
// so an agent scoped to Vigil sees only Vigil's systems.
export default function SonosFleetWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['sonos-summary'],
    queryFn: () => api.get('/sonos/summary').then((r) => r.data),
    refetchInterval: 2 * 60 * 1000,
  });

  const systems = data?.systems || [];

  return (
    <div className="card" style={{ cursor: 'pointer', height: '100%' }} onClick={() => navigate('/sonos')}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Speaker size={16} />
          {t('dashboard.widgets.sonosFleet')}
        </div>
      </div>
      <div style={{ padding: '4px 20px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!systems.length && (
          <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
            {t('sonos.emptyState')}
          </div>
        )}
        {systems.map((s) => {
          const total = parseInt(s.total, 10) || 0;
          const visible = parseInt(s.visible, 10) || 0;
          const orphaned = parseInt(s.orphaned, 10) || 0;
          const down = parseInt(s.down, 10) || 0;
          const allOk = total > 0 && visible === total;
          const recentCut = s.last_power_cut_at &&
            (Date.now() - new Date(s.last_power_cut_at).getTime()) < 24 * 3600 * 1000;
          return (
            <div key={s.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <CompanyLogo name={s.company_display_name} logoUrl={s.company_logo} size={20} />
                <span style={{ fontSize: 13, fontWeight: 600, marginRight: 'auto' }}>{s.name}</span>
                {allOk
                  ? <CheckCircle2 size={16} color="var(--c-resolved)" />
                  : <AlertTriangle size={16} color={down ? 'var(--c-open)' : 'var(--c-pending)'} />}
              </div>
              <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--c-text-muted)', paddingLeft: 28 }}>
                <span style={{ color: 'var(--c-resolved)', fontWeight: 600 }}>{visible}/{total} {t('dashboard.widgets.sonosOk')}</span>
                {orphaned > 0 && (
                  <span style={{ color: 'var(--c-pending)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <Zap size={11} /> {orphaned} {t('sonos.statusOrphaned').toLowerCase()}
                  </span>
                )}
                {down > 0 && (
                  <span style={{ color: 'var(--c-open)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <ZapOff size={11} /> {down} {t('dashboard.widgets.sonosDown')}
                  </span>
                )}
              </div>
              {recentCut && (
                <div style={{ fontSize: 11, color: 'var(--c-pending)', paddingLeft: 28, marginTop: 2 }}>
                  ⚡ {t('dashboard.widgets.sonosRecentCut')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
